import React, { forwardRef } from 'react'
import styled from 'styled-components'

import { useResolveSizeLayout } from '../../../hooks/blockStyle/size'
import { flexMinSizeStyle, getPositionStyle, getSizeStyle } from '../../../utils/layout'
import { useContainerLayoutContext, useFlowLayoutContext } from '../Context'
import type { FlowLayoutNode } from '../types'

type StyledProps = {
    disabled?: boolean
}

const StyledNode = styled.div<StyledProps>`
    position: relative;
    flex-shrink: 0;
    &[data-highlight='true'] {
        background-color: #5551ff1f;
        outline: 2px solid var(--color-main);
        outline-offset: -1px;
    }
`

export interface FlowLayoutBaseNodeProps extends React.ComponentPropsWithoutRef<'div'> {
    data: FlowLayoutNode
    disabled?: boolean
    extra?: React.ReactNode
    onDataDrawerVisible?: (val: boolean) => void
}

export const BaseNode = forwardRef<HTMLDivElement, FlowLayoutBaseNodeProps>((props, ref) => {
    const { data, style, disabled: propDisabled, onDataDrawerVisible, children, extra, ...rest } = props
    const { size, position } = data.data ?? {}
    const parentLayoutCtx = useContainerLayoutContext()

    const { highlight, nodeRender: NodeRender = React.Fragment, sortable } = useFlowLayoutContext()
    const resolvedSize = useResolveSizeLayout(size)

    const mergeStyle: React.CSSProperties = {
        ...flexMinSizeStyle,
        ...getSizeStyle(resolvedSize, parentLayoutCtx.layout?.align?.direction, sortable),
        ...style,
        ...getPositionStyle(position, parentLayoutCtx.level)
    }
    return (
        <StyledNode
            ref={ref}
            style={mergeStyle}
            data-type="node"
            data-node-id={data.id}
            data-highlight={!!highlight?.[data.id]?.self}
            {...rest}
        >
            <NodeRender node={data}>{children}</NodeRender>
            {extra}
        </StyledNode>
    )
})
